import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Paper, 
  Typography, 
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Tooltip,
  CircularProgress,
  Grid,
  Alert,
  TextField,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import { 
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  LinkedIn as LinkedInIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import postService from '../services/postService';
import { useAuth } from '../context/AuthContext';
import { notify } from '../utils/notification';
import axios from 'axios';

const Calendar = () => {
  // Helper function to get current time plus 1 minute in HH:mm format
  const getCurrentTimePlusOneMinute = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState('');
  const [availablePosts, setAvailablePosts] = useState([]);
  const [copySuccess, setCopySuccess] = useState(null);
  const [selectedTime, setSelectedTime] = useState(getCurrentTimePlusOneMinute());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [postToLinkedIn, setPostToLinkedIn] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [linkedInConnected, setLinkedInConnected] = useState(false);

  // Add this useEffect for debugging
  useEffect(() => {
    console.log('User data in Calendar:', user);
    console.log('LinkedIn token exists:', !!user?.linkedin_access_token);
  }, [user]);

  // Add LinkedIn status check
  useEffect(() => {
    const checkLinkedInStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/linkedin-status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setLinkedInConnected(response.data.connected);
      } catch (error) {
        console.error('Error checking LinkedIn status:', error);
        setLinkedInConnected(false);
      }
    };

    if (user) {
      checkLinkedInStatus();
    }
  }, [user]);

  // Define fetchPosts function
  const fetchPosts = async () => {
    if (!user) return;
    
    try {
      const data = await postService.getPosts();
      setPosts(data);
      // Filter out posts that are already scheduled
      const unscheduledPosts = data.filter(post => !post.scheduled_date);
      setAvailablePosts(unscheduledPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      notify.error('Failed to load posts');
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [user]); // Include fetchPosts in dependencies if needed

  useEffect(() => {
    const calendarPost = localStorage.getItem('calendarPost');
    console.log('Calendar received post data:', calendarPost);
    if (calendarPost) {
      try {
        const postData = JSON.parse(calendarPost);
        console.log('Parsed post data:', postData);
        setSelectedPost(postData.id);
        setSelectedDate(new Date());
        setOpenAddDialog(true);
        localStorage.removeItem('calendarPost');
      } catch (error) {
        console.error('Error processing calendar post:', error);
      }
    }
  }, []);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPostsForDate = (date) => {
    if (!date) return [];
    
    return posts.filter(post => {
      if (!post.scheduled_date) return false;
      try {
        const postDate = new Date(post.scheduled_date);
        if (isNaN(postDate.getTime())) return false;
        
        // Compare dates without time component
        const compareDate = new Date(date);
        return postDate.getFullYear() === compareDate.getFullYear() &&
               postDate.getMonth() === compareDate.getMonth() &&
               postDate.getDate() === compareDate.getDate();
      } catch (error) {
        console.error('Error parsing date:', error);
        return false;
      }
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Function to check if selected datetime is in the past
  const isDateTimeInPast = () => {
    if (!selectedDate || !selectedTime) return false;
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const scheduledDate = new Date(selectedDate);
    scheduledDate.setHours(hours, minutes, 0, 0);
    return scheduledDate <= new Date();
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    const postsForDate = getPostsForDate(date);
    setSelectedDate(date);
    setSelectedTime(getCurrentTimePlusOneMinute());
    if (postsForDate.length > 0) {
      setOpenDialog(true);
    } else {
      setOpenAddDialog(true);
    }
  };

  const handleAddPost = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Parse the selected time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Create a date with the selected time
      const localDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        hours,
        minutes,
        0
      );

      // Check if the scheduled time is in the past
      const now = new Date();
      if (localDate <= now) {
        setError('Cannot schedule posts for past dates/times.');
        return;
      }
      
      // Format the date manually to ensure correct date and time
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');
      
      const formattedDate = `${year}-${month}-${day}T${formattedHours}:${formattedMinutes}:00.000Z`;
      
      // Get the full post data to ensure we have attachment info if it's a carousel
      const selectedPostData = availablePosts.find(post => post.id === selectedPost);
      
      console.log('Scheduling post for date and time:', {
        selectedDate: selectedDate.toDateString(),
        selectedTime,
        formattedDate,
        postToLinkedIn,
        postType: selectedPostData?.type,
        hasAttachment: !!selectedPostData?.attachment
      });

      // Schedule the post with the additional LinkedIn posting option
      await postService.schedulePost(selectedPost, formattedDate, postToLinkedIn);
      await fetchPosts();
      setOpenAddDialog(false);
      setSelectedPost('');
      setSelectedTime(getCurrentTimePlusOneMinute());
      setPostToLinkedIn(false);
      
      // Show success notification
      notify.success(
        postToLinkedIn 
          ? 'Post scheduled and will be shared to LinkedIn at the specified time!' 
          : 'Post scheduled successfully!', 
        { 
          autoHideDuration: 4000
        }
      );
    } catch (error) {
      console.error('Error adding post to calendar:', error);
      setError('Failed to add post to calendar. Please try again.');
      notify.error('Failed to schedule post');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(content);
      setTimeout(() => setCopySuccess(null), 2000);
      notify.success('Content copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      notify.error('Failed to copy content');
    }
  };

  const handleRemoveFromCalendar = async (postId) => {
    try {
      setIsLoading(true);
      await postService.schedulePost(postId, null);
      await fetchPosts();
      setOpenDialog(false);
      setConfirmDeleteOpen(false);
      notify.success('Post removed from calendar');
    } catch (error) {
      console.error('Error removing post from calendar:', error);
      setError('Failed to remove post from calendar. Please try again.');
      notify.error('Failed to remove post');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostClick = (post) => {
    setPostToDelete(post);
    setConfirmDeleteOpen(true);
  };

  // Add this function to fetch the latest post status
  const fetchPostStatus = async (postId) => {
    try {
      const response = await postService.getPost(postId);
      return response;
    } catch (error) {
      console.error('Error fetching post status:', error);
      return null;
    }
  };

  // Add this function to refresh posts
  const refreshPosts = async () => {
    setRefreshing(true);
    await fetchPosts();
    setRefreshing(false);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const rows = Math.ceil((daysInMonth + firstDay) / 7);
    
    let currentDay = 1;
    const calendar = [];

    for (let i = 0; i < rows; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < firstDay) {
          week.push(<TableCell key={`empty-${j}`} />);
        } else if (currentDay > daysInMonth) {
          week.push(<TableCell key={`empty-end-${j}`} />);
        } else {
          const dateForCell = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDay
          );
          const postsForDay = getPostsForDate(dateForCell);
          const isToday = new Date().toDateString() === dateForCell.toDateString();
          
          week.push(
            <TableCell 
              key={currentDay} 
              onClick={() => handleDateClick(dateForCell)}
              sx={{ 
                cursor: 'pointer',
                height: {
                  xs: `calc((100vh - 330px) / ${rows})`, // Further reduced for mobile
                  sm: `calc((100vh - 300px) / ${rows})`, // Further reduced for tablets
                  md: `calc((100vh - 250px) / ${rows})`, // Further reduced for desktop
                },
                minHeight: { xs: '70px', md: '90px' }, // Further reduced for smaller calendar
                p: 1,
                verticalAlign: 'top',
                border: isToday ? '2px solid' : '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: isToday ? 700 : postsForDay.length > 0 ? 600 : 400,
                  color: isToday ? 'primary.main' : postsForDay.length > 0 ? 'primary.main' : 'text.primary',
                  mb: 1,
                  display: 'inline-block',
                  width: '24px',
                  height: '24px',
                  lineHeight: '24px',
                  textAlign: 'center',
                  borderRadius: '50%',
                  bgcolor: isToday ? 'var(--primary-color)' : 'transparent',
                  color: isToday ? 'white' : 'inherit'
                }}
              >
                {currentDay}
              </Typography>
              
              {postsForDay.length > 0 && (
                <Box sx={{ 
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  bgcolor: 'var(--primary-color)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {postsForDay.length}
                </Box>
              )}
              
              <Box sx={{ 
                maxHeight: {
                  xs: `calc((100vh - 370px) / ${rows})`, // Further reduced for mobile
                  sm: `calc((100vh - 340px) / ${rows})`, // Further reduced for tablet
                  md: `calc((100vh - 290px) / ${rows})`, // Further reduced for desktop
                },
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px'
                }
              }}>
                {postsForDay.map((post) => (
                  <Box
                    key={post.id}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'primary.main',
                      color: '#ffffff',
                      p: 0.5,
                      borderRadius: 1,
                      mb: 0.5,
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      minHeight: '20px',
                      lineHeight: '1.2',
                      boxSizing: 'border-box'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        color: '#ffffff'
                      }}
                    >
                      {post.topic || 'Untitled Post'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </TableCell>
          );
          currentDay++;
        }
      }
      calendar.push(<TableRow key={i}>{week}</TableRow>);
    }
    return calendar;
  };

  return (
    <>
      <Helmet>
        <title>Content Calendar | Instation</title>
        <meta name="description" content="Plan and schedule your LinkedIn content with our intuitive content calendar." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container" sx={{ 
          height: { xs: 'auto', md: 'calc(100vh - 120px)' }, // Further reduced height
          display: 'flex',
          flexDirection: 'column',
          mb: { xs: 2, md: 3 }, // Reduced bottom margin
          mt: { xs: 0, md: -1 }, // Negative top margin to move up
          maxWidth: { sm: '96%', md: '94%' }, // Make container slightly narrower 
          mx: 'auto' // Center it
        }}>
          <Grid container spacing={3} sx={{ flex: 1 }}>
            <Grid item xs={12} className="content-panel" sx={{ height: '100%' }}>
              <Paper elevation={0} className="content-paper" sx={{ 
                p: 3, 
                borderRadius: 2, 
                border: '1px solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden' // Hide overflow at paper level
              }}>
                {/* Calendar Header */}
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2 // Slightly reduced margin
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={handlePrevMonth}>
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="h6" className="component-heading" sx={{ mb: 0 }}>
                      {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Typography>
                    <IconButton onClick={handleNextMonth}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  
                  <Button 
                    variant="outlined" 
                    startIcon={<EventIcon />}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      color: 'var(--primary-color)',
                      borderColor: 'var(--primary-color)',
                      '&:hover': {
                        borderColor: 'var(--primary-dark)',
                        backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)'
                      }
                    }}
                    onClick={() => {
                      setSelectedDate(new Date());
                      setOpenAddDialog(true);
                    }}
                  >
                    Schedule Post
                  </Button>
                </Box>

                {/* Calendar Table */}
                <TableContainer sx={{ 
                  flex: 1,
                  overflow: 'auto', // Allow table to scroll if needed
                  height: { xs: 'calc(100vh - 230px)', md: 'auto' }, // Further reduced height
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <Table sx={{ 
                    height: '100%',
                    tableLayout: 'fixed',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <TableHead sx={{ display: 'table', width: '100%', tableLayout: 'fixed' }}>
                      <TableRow>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                          <TableCell 
                            key={day} 
                            align="center"
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.secondary',
                              borderBottom: '2px solid',
                              borderColor: 'divider',
                              py: 1,
                              width: `${100/7}%`
                            }}
                          >
                            {day}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ 
                      height: '100%',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      '& tr': {
                        display: 'table',
                        width: '100%',
                        tableLayout: 'fixed',
                        flex: 1
                      }
                    }}>
                      {renderCalendar()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Posts for Date Dialog */}
          <Dialog 
            open={openDialog} 
            onClose={() => !isLoading && setOpenDialog(false)}
            maxWidth="md"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: 2,
                maxHeight: '90vh',
                margin: { xs: 2, sm: 4 },
                width: '95%',
                maxWidth: '900px'
              }
            }}
          >
            <DialogTitle sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2,
              px: 3
            }}>
              <Typography variant="h6" className="component-heading" sx={{ 
                mb: 0,
                fontSize: '1.25rem',
                fontWeight: 600
              }}>
                Posts for {selectedDate?.toLocaleDateString()}
              </Typography>
              <Box>
                <IconButton 
                  size="small" 
                  onClick={refreshPosts}
                  disabled={refreshing}
                  sx={{ mr: 1 }}
                >
                  {refreshing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                    borderColor: 'var(--primary-color)',
                    '&:hover': {
                      borderColor: 'var(--primary-dark)',
                      backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)'
                    }
                  }}
                  onClick={() => {
                    setOpenDialog(false);
                    setOpenAddDialog(true);
                  }}
                >
                  Add Post
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3.5 }}>
              {selectedDate && getPostsForDate(selectedDate)?.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  color: 'text.secondary'
                }}>
                  <EventIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>No posts scheduled</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Click the "Add Post" button to schedule a post for this date.
                  </Typography>
                </Box>
              ) : (
                selectedDate && getPostsForDate(selectedDate)?.map((post) => (
                <Card key={post.id} sx={{ 
                  mb: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(var(--primary-color-rgb), 0.02)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      mb: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                        >
                          {post.topic || 'Untitled Post'}
                        </Typography>

                        {/* Post type indicators */}
                        {post.type === 'carousel' && (
                          <Tooltip title="Carousel Post">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                              color: 'var(--primary-color)',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}>
                              Carousel
                            </Box>
                          </Tooltip>
                        )}

                        {/* LinkedIn status indicators */}
                        {Boolean(post.post_to_linkedin) && !Boolean(post.posted_to_linkedin) && (
                          <Tooltip title="Will be posted to LinkedIn">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              ml: 1,
                              bgcolor: '#0A66C2',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem'
                            }}>
                              <LinkedInIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Will post to LinkedIn
                            </Box>
                          </Tooltip>
                        )}
                        
                        {Boolean(post.posted_to_linkedin) && (
                          <Tooltip title="Posted to LinkedIn">
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              ml: 1,
                              bgcolor: '#0A66C2',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem'
                            }}>
                              <LinkedInIcon fontSize="small" sx={{ mr: 0.5 }} />
                              Posted
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Copy Content">
                          <IconButton 
                            size="small"
                            onClick={() => handleCopy(post.content)}
                            color={copySuccess === post.content ? "success" : "default"}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from Calendar">
                          <IconButton 
                            size="small"
                            onClick={() => handlePostClick(post)}
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                      
                      {/* Image display */}
                      {post.image && !post.type === 'carousel' && (
                        <Box sx={{ 
                          my: 2, 
                          textAlign: 'center',
                          '& img': {
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            objectFit: 'contain',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                          }
                        }}>
                          <img 
                            src={post.image} 
                            alt="Post"
                          />
                        </Box>
                      )}

                      {/* PDF Attachment display for carousel posts */}
                      {post.type === 'carousel' && post.attachment && (
                        <Box sx={{ 
                          my: 2,
                          width: '100%',
                          height: '300px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                        }}>
                          <iframe
                            src={`data:application/pdf;base64,${post.attachment}`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title="Carousel PDF"
                          />
                        </Box>
                      )}
                      
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        fontSize: '0.95rem',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      {post.content}
                    </Typography>
                      
                      {/* Bottom status indicators */}
                      <Box sx={{ 
                        mt: 2, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                        >
                          Scheduled for: {new Date(post.scheduled_date).toLocaleString()}
                        </Typography>
                        
                        {Boolean(post.post_to_linkedin) && !Boolean(post.posted_to_linkedin) && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              color: '#0A66C2',
                              fontWeight: 500
                            }}
                          >
                            <LinkedInIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Will post to LinkedIn
                          </Typography>
                        )}
                        
                        {Boolean(post.posted_to_linkedin) && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              color: '#0A66C2',
                              fontWeight: 500
                            }}
                          >
                            <LinkedInIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Posted to LinkedIn
                          </Typography>
                        )}
                      </Box>
                  </CardContent>
                </Card>
                ))
              )}
            </DialogContent>
          </Dialog>

          {/* Add Post Dialog */}
          <Dialog 
            open={openAddDialog} 
            onClose={() => !isLoading && setOpenAddDialog(false)}
            maxWidth="lg"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: 2,
                maxHeight: '90vh',
                margin: { xs: 2, sm: 4 },
                width: '95%',
                maxWidth: '1200px'
              }
            }}
          >
            <DialogTitle sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2,
              px: 3,
              fontSize: '1.25rem',
              fontWeight: 600
            }}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                    Add Post to {selectedDate?.toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                    Post Preview
                  </Typography>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Grid container>
                {/* Left side - Post selection and scheduling */}
                <Grid item xs={12} md={6} sx={{ 
                  p: 3.5 
                }}>
                  {availablePosts.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You don't have any unscheduled posts. Create a post first to schedule it.
                    </Alert>
                  ) : (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Select Post</InputLabel>
                        <Select
                          value={selectedPost}
                          onChange={(e) => setSelectedPost(e.target.value)}
                          disabled={isLoading}
                        >
                          {availablePosts.map((post) => (
                            <MenuItem key={post.id} value={post.id}>
                              {post.topic || post.content.substring(0, 50) + '...'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" className="section-title" sx={{ fontWeight: 500, color: 'black', mb: 1 }}>
                          Schedule Time
                        </Typography>
                        <TextField
                          type="time"
                          value={selectedTime}
                          onChange={(e) => setSelectedTime(e.target.value)}
                          fullWidth
                          disabled={isLoading}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 300, // 5 min
                          }}
                        />
                      </Box>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={postToLinkedIn}
                            onChange={(e) => setPostToLinkedIn(e.target.checked)}
                            color="primary"
                            disabled={isLoading || !linkedInConnected}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LinkedInIcon sx={{ mr: 1, color: '#0A66C2' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Automatically post to LinkedIn at scheduled time
                            </Typography>
                          </Box>
                        }
                      />
                      
                      {!linkedInConnected && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Connect your LinkedIn account in your profile settings to enable automatic posting.
                        </Alert>
                      )}

                      {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {error}
                        </Alert>
                      )}

                      {isDateTimeInPast() && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Selected date and time is in the past. Please choose a future date/time.
                        </Alert>
                      )}
                    </>
                  )}
                </Grid>

                {/* Right side - Post preview */}
                <Grid item xs={12} md={6} sx={{ 
                  p: 3.5, 
                  display: 'flex',
                  flexDirection: 'column',
                  height: { md: '600px' }
                }}>
                  {selectedPost ? (
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      {/* Fixed Header */}
                      <Box sx={{ mb: 3 }}>
                        {/* Show image for regular posts */}
                        {selectedPost && availablePosts.find(post => post.id === selectedPost)?.image && 
                         availablePosts.find(post => post.id === selectedPost)?.type !== 'carousel' && (
                          <Box sx={{ 
                            mb: 3,
                            '& img': {
                              width: '100%',
                              maxHeight: '300px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                            }
                          }}>
                            <img 
                              src={availablePosts.find(post => post.id === selectedPost)?.image} 
                              alt="Post preview" 
                            />
                          </Box>
                        )}
                        
                        {/* Show PDF for carousel posts */}
                        {selectedPost && availablePosts.find(post => post.id === selectedPost)?.type === 'carousel' && 
                         availablePosts.find(post => post.id === selectedPost)?.attachment && (
                          <Box sx={{ 
                            mb: 3,
                            width: '100%',
                            height: '300px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                          }}>
                            <iframe
                              src={`data:application/pdf;base64,${availablePosts.find(post => post.id === selectedPost)?.attachment}`}
                              width="100%"
                              height="100%"
                              style={{ border: 'none' }}
                              title="Carousel PDF"
                            />
                          </Box>
                        )}
                        
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                          {availablePosts.find(post => post.id === selectedPost)?.topic || 'Untitled Post'}
                          {availablePosts.find(post => post.id === selectedPost)?.type === 'carousel' && (
                            <Box component="span" sx={{ 
                              display: 'inline-block',
                              ml: 1,
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                              color: 'var(--primary-color)',
                              borderRadius: 1
                            }}>
                              Carousel
                            </Box>
                          )}
                        </Typography>
                      </Box>

                      {/* Scrollable Content */}
                      <Box sx={{ 
                        flex: 1,
                        overflow: 'auto',
                        mb: 3,
                        pr: 2,
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#888',
                          borderRadius: '4px',
                          '&:hover': {
                            background: '#666',
                          },
                        },
                      }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            color: 'text.primary',
                            fontSize: '0.95rem'
                          }}
                        >
                          {availablePosts.find(post => post.id === selectedPost)?.content}
                        </Typography>
                      </Box>

                      {/* Fixed Footer */}
                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: 'text.primary'
                        }}>
                          <EventIcon fontSize="small" />
                          <Typography variant="body2">
                            Will be scheduled for: {selectedDate?.toLocaleDateString()} at {selectedTime}
                          </Typography>
                        </Box>
                        {postToLinkedIn && (
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#0A66C2',
                            mt: 1
                          }}>
                            <LinkedInIcon fontSize="small" />
                            <Typography variant="body2">
                              Will be posted to LinkedIn
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.primary',
                      py: 8
                    }}>
                      <EventIcon sx={{ fontSize: 48, mb: 2, color: 'action.disabled' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Select a post to preview
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        The post content will appear here
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button onClick={() => setOpenAddDialog(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddPost} 
                variant="contained" 
                disabled={!selectedPost || isLoading || availablePosts.length === 0 || isDateTimeInPast()}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Adding...
                  </>
                ) : (
                  'Schedule Post'
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirm Delete Dialog */}
          <Dialog
            open={confirmDeleteOpen}
            onClose={() => !isLoading && setConfirmDeleteOpen(false)}
          >
            <DialogTitle>
              <Typography variant="h6" className="component-heading" sx={{ mb: 0 }}>
                Remove Post from Calendar
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography>
                Are you sure you want to remove this post from the calendar?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setConfirmDeleteOpen(false)} 
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleRemoveFromCalendar(postToDelete?.id)}
                color="error"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </>
  );
};

export default Calendar; 